import { useEffect, useMemo, useState } from 'react'
import {
  Bell,
  BookOpen,
  CheckCheck,
  GraduationCap,
  Loader2,
  RefreshCw,
  Send,
  Shield,
  Trash2,
  Users,
  X,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { classesApi, notificationsApi } from '../api/services.js'
import PageHeader from '../components/PageHeader.jsx'
import { useAuth } from '../hooks/useAuth.js'
import {
  getEntityId,
  getNotificationId,
  isNotificationReadByUser,
  markNotificationReadByUser,
  markNotificationsReadByUser,
  normalizeNotificationList,
  removeNotificationById,
  upsertNotification,
} from '../utils/notifications.js'
import { connectMessageSocket } from '../services/messageSocket.js'

const audienceOptions = [
  {
    value: 'all',
    label: 'Everyone',
    icon: Bell,
    dotClass: 'bg-blue-500',
    chipClass: 'bg-blue-50 text-blue-700',
    activeClass: 'border-blue-200 bg-blue-50 text-blue-700',
  },
  {
    value: 'student',
    label: 'Students',
    icon: GraduationCap,
    dotClass: 'bg-green-500',
    chipClass: 'bg-green-50 text-green-700',
    activeClass: 'border-green-200 bg-green-50 text-green-700',
  },
  {
    value: 'teacher',
    label: 'Teachers',
    icon: Users,
    dotClass: 'bg-cyan-500',
    chipClass: 'bg-cyan-50 text-cyan-700',
    activeClass: 'border-cyan-200 bg-cyan-50 text-cyan-700',
  },
  {
    value: 'parent',
    label: 'Parents',
    icon: Users,
    dotClass: 'bg-amber-500',
    chipClass: 'bg-amber-50 text-amber-700',
    activeClass: 'border-amber-200 bg-amber-50 text-amber-700',
  },
  {
    value: 'admin',
    label: 'Admins',
    icon: Shield,
    dotClass: 'bg-violet-500',
    chipClass: 'bg-violet-50 text-violet-700',
    activeClass: 'border-violet-200 bg-violet-50 text-violet-700',
  },
]

const priorityOptions = [
  {
    value: 'low',
    label: 'Low',
    chipClass: 'bg-slate-100 text-slate-700',
    activeClass: 'border-slate-300 bg-slate-50 text-slate-700',
  },
  {
    value: 'normal',
    label: 'Normal',
    chipClass: 'bg-blue-50 text-blue-700',
    activeClass: 'border-blue-200 bg-blue-50 text-blue-700',
  },
  {
    value: 'high',
    label: 'High',
    chipClass: 'bg-red-50 text-red-700',
    activeClass: 'border-red-200 bg-red-50 text-red-700',
  },
]

const deliveryOptions = [
  {
    value: 'both',
    label: 'Both',
    description: 'Show inside the app and also send a push notification.',
    chipClass: 'bg-primary-50 text-primary-700',
    activeClass: 'border-primary-200 bg-primary-50 text-primary-700',
  },
  {
    value: 'in_app',
    label: 'In-app only',
    description: 'Visible only in the app notification center.',
    chipClass: 'bg-slate-100 text-slate-700',
    activeClass: 'border-slate-300 bg-slate-50 text-slate-700',
  },
  {
    value: 'push',
    label: 'Push only',
    description: 'Send outside the app without saving it in the in-app notification list.',
    chipClass: 'bg-amber-50 text-amber-700',
    activeClass: 'border-amber-200 bg-amber-50 text-amber-700',
  },
]

const emptyForm = {
  title: '',
  message: '',
  targetRole: 'all',
  priority: 'normal',
  delivery: 'both',
  classId: '',
}

const getAudienceOption = (value) => (
  audienceOptions.find((option) => option.value === String(value || 'all').toLowerCase()) || audienceOptions[0]
)

const getPriorityOption = (value) => (
  priorityOptions.find((option) => option.value === String(value || 'normal').toLowerCase()) || priorityOptions[1]
)

const getDeliveryOption = (value) => (
  deliveryOptions.find((option) => option.value === String(value || 'in_app').toLowerCase()) || deliveryOptions[1]
)

const getClassLabel = (classItem, classLookup = {}) => {
  if (!classItem) return ''

  if (typeof classItem === 'string') {
    return classLookup[classItem] || ''
  }

  const classId = classItem._id || classItem.id
  if (classId && classLookup[classId]) return classLookup[classId]

  const name = classItem.name || classItem.className || classItem.label || ''
  return [name, classItem.section].filter(Boolean).join(' — ')
}

const formatDate = (value) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }).format(date)
}

const formatDateTime = (value) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

const getSender = (notification) => {
  const sender = notification?.createdBy
  if (!sender) return 'Admin'
  if (typeof sender === 'string') return sender
  const name = `${sender.firstName || ''} ${sender.lastName || ''}`.trim()
  return name || sender.name || sender.email || 'Admin'
}

function SelectionChip({ label, icon: Icon, active, activeClass, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-2 text-sm font-medium transition-colors ${
        active
          ? activeClass
          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:text-gray-900'
      }`}
    >
      <span className="flex items-center gap-2">
        {Icon ? <Icon className="w-4 h-4" /> : null}
        <span>{label}</span>
      </span>
    </button>
  )
}

function SkeletonCard() {
  return (
    <div className="card p-4 animate-pulse space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 space-y-2">
          <div className="h-4 rounded bg-gray-100 w-1/3" />
          <div className="h-3 rounded bg-gray-100 w-full" />
        </div>
        <div className="h-3 rounded bg-gray-100 w-20" />
      </div>
      <div className="flex gap-2">
        <div className="h-6 rounded-full bg-gray-100 w-20" />
        <div className="h-6 rounded-full bg-gray-100 w-16" />
        <div className="h-6 rounded-full bg-gray-100 w-24" />
      </div>
    </div>
  )
}

export default function NotificationsPage() {
  const { user, token } = useAuth()
  const currentUserId = getEntityId(user)
  const currentSchoolId = getEntityId(user?.schoolId || user?.school)
  const canCreateNotifications = user?.role === 'admin' || user?.role === 'teacher'
  const canDeleteNotifications = user?.role === 'admin'
  const [notifications, setNotifications] = useState([])
  const [classes, setClasses] = useState([])
  const [loading, setLoading] = useState(true)
  const [classesLoading, setClassesLoading] = useState(false)
  const [composeOpen, setComposeOpen] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [deleteId, setDeleteId] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [sending, setSending] = useState(false)
  const [form, setForm] = useState(emptyForm)

  const classLookup = useMemo(
    () => Object.fromEntries(
      classes.map((item) => [
        String(item._id || item.id),
        [item.name || item.className || 'Class', item.section].filter(Boolean).join(' — '),
      ])
    ),
    [classes]
  )

  const unreadCount = useMemo(
    () => notifications.filter((item) => !isNotificationReadByUser(item, currentUserId)).length,
    [currentUserId, notifications]
  )

  const highPriorityCount = useMemo(
    () => notifications.filter((item) => getPriorityOption(item.priority).value === 'high').length,
    [notifications]
  )

  const selectedNotification = useMemo(
    () => notifications.find((item) => getNotificationId(item) === selectedId) || null,
    [notifications, selectedId]
  )

  const fetchNotifications = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true)
      const data = await notificationsApi.getAll()
      setNotifications(normalizeNotificationList(data))
    } catch (err) {
      if (!silent) {
        toast.error(err.response?.data?.message || 'Failed to load notifications')
        setNotifications([])
      }
    } finally {
      if (!silent) setLoading(false)
    }
  }

  const fetchClasses = async ({ silent = false } = {}) => {
    try {
      if (!silent) setClassesLoading(true)
      const data = await classesApi.getAll()
      setClasses(Array.isArray(data) ? data : data?.classes ?? [])
    } catch (err) {
      if (!silent) {
        toast.error(err.response?.data?.message || 'Failed to load classes')
      }
    } finally {
      if (!silent) setClassesLoading(false)
    }
  }

  useEffect(() => {
    fetchNotifications()
    fetchClasses({ silent: true })
  }, [])

  useEffect(() => {
    if (!token || !currentUserId) return undefined

    const socket = connectMessageSocket(token)
    if (!socket) return undefined

    const sameSchoolEvent = (payload) => (
      !payload?.schoolId
      || !currentSchoolId
      || String(payload.schoolId) === String(currentSchoolId)
    )

    const handleNotificationNew = (payload) => {
      if (!sameSchoolEvent(payload)) return
      const notification = payload?.notification
      if (!getNotificationId(notification)) return
      setNotifications((current) => upsertNotification(current, notification))
    }

    const handleNotificationRead = (payload) => {
      if (!sameSchoolEvent(payload) || !payload?.notificationId || !payload?.userId) return
      setNotifications((current) => markNotificationReadByUser(
        current,
        String(payload.notificationId),
        String(payload.userId),
      ))
    }

    const handleNotificationsReadAll = (payload) => {
      if (!sameSchoolEvent(payload) || String(payload?.userId || '') !== currentUserId) return
      setNotifications((current) => markNotificationsReadByUser(
        current,
        payload?.notificationIds || [],
        currentUserId,
      ))
    }

    const handleNotificationDeleted = (payload) => {
      if (!sameSchoolEvent(payload) || !payload?.notificationId) return
      setNotifications((current) => removeNotificationById(current, String(payload.notificationId)))
    }

    // Keep this page in sync with the header bell without polling the notification API.
    socket.on('notification:new', handleNotificationNew)
    socket.on('notification:read', handleNotificationRead)
    socket.on('notification:read-all', handleNotificationsReadAll)
    socket.on('notification:deleted', handleNotificationDeleted)

    return () => {
      socket.off('notification:new', handleNotificationNew)
      socket.off('notification:read', handleNotificationRead)
      socket.off('notification:read-all', handleNotificationsReadAll)
      socket.off('notification:deleted', handleNotificationDeleted)
    }
  }, [currentSchoolId, currentUserId, token])

  useEffect(() => {
    if (!selectedId) return
    if (notifications.some((item) => getNotificationId(item) === selectedId)) return
    setSelectedId(null)
  }, [notifications, selectedId])

  const openComposeModal = () => {
    setComposeOpen(true)
    if (!classes.length && !classesLoading) {
      fetchClasses({ silent: false })
    }
  }

  const handleSend = async () => {
    if (!form.title.trim() || !form.message.trim()) {
      toast.error('Title and message are required')
      return
    }

    const payload = {
      title: form.title.trim(),
      message: form.message.trim(),
      targetRole: form.targetRole,
      priority: form.priority,
      delivery: form.delivery,
      ...(form.classId ? { classId: form.classId } : {}),
    }

    try {
      setSending(true)
      const created = await notificationsApi.send(payload)
      const createdNotification = created?.notification && !getNotificationId(created)
        ? created.notification
        : created

      if (form.delivery !== 'push') {
        if (createdNotification && getNotificationId(createdNotification)) {
          setNotifications((current) => upsertNotification(current, createdNotification))
        } else {
          await fetchNotifications({ silent: true })
        }
      }

      toast.success(`${getDeliveryOption(form.delivery).label} notification sent successfully`)
      setComposeOpen(false)
      setForm(emptyForm)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send notification')
    } finally {
      setSending(false)
    }
  }

  const handleOpenNotification = async (notification) => {
    const notificationId = getNotificationId(notification)
    if (!notificationId) return

    setSelectedId(notificationId)

    if (isNotificationReadByUser(notification, currentUserId)) return

    try {
      await notificationsApi.markAsRead(notificationId)
      setNotifications((current) => markNotificationReadByUser(current, notificationId, currentUserId))
    } catch {}
  }

  const handleMarkAllRead = async () => {
    if (!currentUserId || unreadCount === 0) return

    try {
      await notificationsApi.markAllRead()
      setNotifications((current) => markNotificationsReadByUser(current, [], currentUserId))
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to mark notifications as read')
    }
  }

  const handleDelete = async () => {
    if (!deleteId) return

    try {
      setDeleting(true)
      await notificationsApi.remove(deleteId)
      setNotifications((current) => removeNotificationById(current, deleteId))
      setDeleteId(null)
      toast.success('Notification deleted')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete notification')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Notifications"
        subtitle={
          canCreateNotifications
            ? 'Review school alerts and send the same priority, delivery, and class-targeted notifications used in the Edubridge app.'
            : 'Review school alerts with the same read state and delivery metadata used in the Edubridge app.'
        }
        chips={[
          `${notifications.length} total`,
          `${unreadCount} unread`,
          `${highPriorityCount} high priority`,
        ]}
        actions={(
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => fetchNotifications()} className="btn-secondary p-2" title="Refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={handleMarkAllRead}
              disabled={unreadCount === 0}
              className="btn-secondary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCheck className="w-4 h-4" />
              Mark All Read
            </button>
            {canCreateNotifications && (
              <button type="button" onClick={openComposeModal} className="btn-primary flex items-center gap-2">
                <Send className="w-4 h-4" />
                Send Notification
              </button>
            )}
          </div>
        )}
      />

      <div className="space-y-3">
        {loading ? (
          [...Array(4)].map((_, index) => <SkeletonCard key={index} />)
        ) : notifications.length === 0 ? (
          <div className="card p-10 text-center">
            <div className="mx-auto w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
              <Bell className="w-5 h-5" />
            </div>
            <h3 className="mt-4 text-base font-semibold text-gray-900">No notifications yet</h3>
            <p className="mt-2 text-sm text-gray-500">
              {canCreateNotifications
                ? 'Send your first notification to students, parents, teachers, or admins.'
                : 'Notifications will appear here once your team starts sending school updates.'}
            </p>
          </div>
        ) : notifications.map((notification) => {
          const notificationId = getNotificationId(notification)
          const audience = getAudienceOption(notification.targetRole)
          const priority = getPriorityOption(notification.priority)
          const delivery = getDeliveryOption(notification.delivery)
          const classLabel = getClassLabel(notification.classId || notification.class, classLookup)
          const isUnread = !isNotificationReadByUser(notification, currentUserId)

          return (
            <div
              key={notificationId}
              className={`card p-0 overflow-hidden border transition-colors ${
                isUnread ? 'border-primary-200 bg-primary-50/40' : 'border-gray-100'
              }`}
            >
              <div className="flex items-start gap-3 px-4 py-4">
                <button
                  type="button"
                  onClick={() => handleOpenNotification(notification)}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="flex items-start gap-3">
                    <span className={`mt-2 h-2.5 w-2.5 rounded-full flex-shrink-0 ${audience.dotClass}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-1.5">
                            <h3 className={`text-sm text-gray-900 ${isUnread ? 'font-semibold' : 'font-medium'}`}>
                              {notification.title}
                            </h3>
                            {isUnread && <span className="h-2 w-2 rounded-full bg-primary-500 flex-shrink-0" />}
                          </div>
                          <p className="text-sm text-gray-600 leading-relaxed line-clamp-2">
                            {notification.message}
                          </p>
                        </div>
                        <p className="text-xs text-gray-400 whitespace-nowrap">
                          {formatDate(notification.createdAt || notification.sentAt)}
                        </p>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${audience.chipClass}`}>
                          {audience.label}
                        </span>
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${priority.chipClass}`}>
                          {priority.label}
                        </span>
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${delivery.chipClass}`}>
                          {delivery.label}
                        </span>
                        {classLabel && (
                          <span className="text-xs font-semibold px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                            {classLabel}
                          </span>
                        )}
                        <span className="text-xs text-gray-400">
                          By {getSender(notification)}
                        </span>
                        {Array.isArray(notification.readBy) && (
                          <span className="text-xs text-gray-400">
                            {notification.readBy.length} read
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>

                {canDeleteNotifications && (
                  <button
                    type="button"
                    onClick={() => setDeleteId(notificationId)}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
                    title="Delete notification"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {composeOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4">
          <div className="flex min-h-full items-start justify-center py-4 sm:items-center">
            <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">Send Notification</p>
                <h3 className="text-lg font-bold text-gray-900 mt-1">Match the Edubridge app flow</h3>
              </div>
              <button type="button" onClick={() => setComposeOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-6 space-y-5">
              <div className="card p-5 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Title *</label>
                  <input
                    className="input-field w-full"
                    value={form.title}
                    onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                    placeholder="e.g. Holiday Notice"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Message *</label>
                  <textarea
                    className="input-field w-full h-32 resize-none"
                    value={form.message}
                    onChange={(event) => setForm((current) => ({ ...current, message: event.target.value }))}
                    placeholder="Write your message..."
                  />
                </div>
              </div>

              <div className="card p-5">
                <p className="text-sm font-semibold text-gray-900 mb-3">Send To</p>
                <div className="flex flex-wrap gap-2">
                  {audienceOptions.map((option) => (
                    <SelectionChip
                      key={option.value}
                      label={option.label}
                      icon={option.icon}
                      active={form.targetRole === option.value}
                      activeClass={option.activeClass}
                      onClick={() => setForm((current) => ({ ...current, targetRole: option.value }))}
                    />
                  ))}
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-5">
                <div className="card p-5">
                  <p className="text-sm font-semibold text-gray-900 mb-3">Priority</p>
                  <div className="flex flex-wrap gap-2">
                    {priorityOptions.map((option) => (
                      <SelectionChip
                        key={option.value}
                        label={option.label}
                        active={form.priority === option.value}
                        activeClass={option.activeClass}
                        onClick={() => setForm((current) => ({ ...current, priority: option.value }))}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    High priority notifications open automatically once when the user next opens the app.
                  </p>
                </div>

                <div className="card p-5">
                  <p className="text-sm font-semibold text-gray-900 mb-3">Delivery</p>
                  <div className="flex flex-wrap gap-2">
                    {deliveryOptions.map((option) => (
                      <SelectionChip
                        key={option.value}
                        label={option.label}
                        active={form.delivery === option.value}
                        activeClass={option.activeClass}
                        onClick={() => setForm((current) => ({ ...current, delivery: option.value }))}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    {getDeliveryOption(form.delivery).description} Push works only for users who allowed notifications on their device.
                  </p>
                </div>
              </div>

              <div className="card p-5">
                <div className="flex items-center gap-2 mb-3">
                  <BookOpen className="w-4 h-4 text-primary-600" />
                  <p className="text-sm font-semibold text-gray-900">Specific Class</p>
                  <span className="text-xs text-gray-400">(optional)</span>
                </div>
                {classesLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading classes...
                  </div>
                ) : classes.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No classes available to target yet. This notification will go to the selected audience across the school.
                  </p>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto pr-1">
                      <SelectionChip
                        label="All Classes"
                        active={!form.classId}
                        activeClass="border-primary-200 bg-primary-50 text-primary-700"
                        onClick={() => setForm((current) => ({ ...current, classId: '' }))}
                      />
                      {classes.map((item) => {
                        const classId = String(item._id || item.id)
                        return (
                          <SelectionChip
                            key={classId}
                            label={classLookup[classId]}
                            active={form.classId === classId}
                            activeClass="border-primary-200 bg-primary-50 text-primary-700"
                            onClick={() => setForm((current) => ({ ...current, classId }))}
                          />
                        )
                      })}
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      Use a class-specific notification for exam schedules, class events, or fee reminders for a particular section.
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className="px-6 py-5 border-t border-gray-100 flex flex-wrap gap-3 justify-end">
              <button type="button" onClick={() => setComposeOpen(false)} className="btn-secondary" disabled={sending}>
                Cancel
              </button>
              <button type="button" onClick={handleSend} disabled={sending} className="btn-primary flex items-center gap-2">
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Notification
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        </div>
      )}

      {selectedNotification && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4">
          <div className="flex min-h-full items-start justify-center py-4 sm:items-center">
            <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-gray-100">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">
                  {getPriorityOption(selectedNotification.priority).value === 'high' ? 'Important Notification' : 'Notification'}
                </p>
                <h3 className="text-xl font-bold text-gray-900 mt-1">{selectedNotification.title}</h3>
              </div>
              <button type="button" onClick={() => setSelectedId(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-6 space-y-5">
              <div className="flex flex-wrap gap-2">
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${getAudienceOption(selectedNotification.targetRole).chipClass}`}>
                  {getAudienceOption(selectedNotification.targetRole).label}
                </span>
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${getPriorityOption(selectedNotification.priority).chipClass}`}>
                  {getPriorityOption(selectedNotification.priority).label}
                </span>
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${getDeliveryOption(selectedNotification.delivery).chipClass}`}>
                  {getDeliveryOption(selectedNotification.delivery).label}
                </span>
                {getClassLabel(selectedNotification.classId || selectedNotification.class, classLookup) && (
                  <span className="text-xs font-semibold px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                    {getClassLabel(selectedNotification.classId || selectedNotification.class, classLookup)}
                  </span>
                )}
              </div>

              <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-4">
                <p className="text-sm text-gray-700 leading-7 whitespace-pre-wrap">
                  {selectedNotification.message}
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div className="rounded-2xl border border-gray-100 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">Sent By</p>
                  <p className="text-sm font-medium text-gray-900 mt-2">{getSender(selectedNotification)}</p>
                </div>
                <div className="rounded-2xl border border-gray-100 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">Sent On</p>
                  <p className="text-sm font-medium text-gray-900 mt-2">
                    {formatDateTime(selectedNotification.createdAt || selectedNotification.sentAt)}
                  </p>
                </div>
                <div className="rounded-2xl border border-gray-100 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">Read Status</p>
                  <p className="text-sm font-medium text-gray-900 mt-2">
                    {Array.isArray(selectedNotification.readBy) ? `${selectedNotification.readBy.length} user(s) read` : 'Read count unavailable'}
                  </p>
                </div>
                <div className="rounded-2xl border border-gray-100 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">Audience</p>
                  <p className="text-sm font-medium text-gray-900 mt-2">
                    {getAudienceOption(selectedNotification.targetRole).label}
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 py-5 border-t border-gray-100 flex justify-end">
              <button type="button" onClick={() => setSelectedId(null)} className="btn-primary">
                Close
              </button>
            </div>
          </div>
        </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-5 h-5 text-red-500" />
            </div>
            <h3 className="font-bold text-gray-900 mb-1">Delete Notification?</h3>
            <p className="text-sm text-gray-500 mb-5">This cannot be undone.</p>
            <div className="flex gap-3">
              <button type="button" onClick={() => setDeleteId(null)} className="btn-secondary flex-1" disabled={deleting}>
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
